import React, { useState, useEffect } from "react";
import { bool, func, string } from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import get from "lodash/get";
import { sendOtp, resetPassword, logout } from "@quintype/bridgekeeper-js";
import { navigateTo } from "../../../arrow/utils/utils";
import { MEMBER_UPDATED } from "../../store/actions";
import { Icon } from "../../atoms/icon";
import Button from "../../atoms/Button";
import { RESET_PASSWORD_CONTENT } from "../../../constants";
import "./reset-password-modal.m.css";
import EditIcon from "../../../../assets/icons/edit";
import OpenEye from "../../../../assets/icons/open-eye";
import OffEye from "../../../../assets/icons/off-eye";

// Step constants
const STEPS = {
  EMAIL_INPUT: 'email-input',
  OTP_PASSWORD: 'otp-password',
  SUCCESS: 'success',
  ERROR: 'error'
};

export const ResetPasswordModal = ({
  isOpen,
  onClose,
  className = "",
  ...props
}) => {
  const dispatch = useDispatch();
  const member = useSelector((state) => get(state, ["member"], null));
  const memberEmail = get(member, ["email"], "");

  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL_INPUT);
  const [formData, setFormData] = useState({
    email: memberEmail,
    otp: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const wrongOtpError = ({ action, errorMsg }) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_signup_error",
        event_action: action,
        error_message: errorMsg,
      });
    }
  };

  const handleLogoutAndRedirect = async () => {
    try {
      // If user is logged in, log them out
      if (member) {
        await logout();
        dispatch({ type: MEMBER_UPDATED, member: null });
      }
      // Close the modal first
      onClose();
      // Redirect to homepage
      navigateTo(dispatch, "/");
    } catch (error) {
      console.error('Error during logout:', error);
      // Still redirect even if logout fails
      onClose();
      navigateTo(dispatch, "/");
    }
  };

  // Timer effect for resend OTP
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
    } else if (resendTimer === 0 && currentStep === STEPS.OTP_PASSWORD) {
      setIsResendEnabled(true);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, currentStep]);

  // Reset form when modal opens and populate email
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEPS.EMAIL_INPUT);
      setFormData({ email: memberEmail || '', otp: '', password: '' });
      setErrors({});
      setIsLoading(false);
      setIsPasswordVisible(false);
      setIsEmailEditable(!memberEmail); // If no member email, make it editable
      setResendTimer(0);
      setIsResendEnabled(false);
    }
  }, [isOpen, memberEmail]);

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const handleGetOTP = async () => {
    // Validate email
    if (!formData.email) {
      setErrors({ email: 'Email is required' });
      return;
    }
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setIsLoading(true);
    try {
      await sendOtp({ email: formData.email });
      setCurrentStep(STEPS.OTP_PASSWORD);
      setResendTimer(180); // 3 minutes = 180 seconds
      setIsResendEnabled(false);
    } catch (error) {
      console.error('Error sending OTP:', error);
      setErrors({ email: 'Failed to send OTP. Please try again.' });
      wrongOtpError({ action: "send_otp", errorMsg: error.message || 'Failed to send OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!isResendEnabled) return;

    setIsLoading(true);
    try {
      await sendOtp({ email: formData.email });
      setResendTimer(180); // Reset timer to 3 minutes
      setIsResendEnabled(false);
      // Clear any OTP errors
      setErrors(prev => ({ ...prev, otp: '' }));
    } catch (error) {
      console.error('Error resending OTP:', error);
      setErrors(prev => ({ ...prev, otp: 'Failed to resend OTP. Please try again.' }));
      wrongOtpError({ action: "wrong_resend_otp", errorMsg: error.message || 'Failed to resend OTP. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    // Validate inputs
    const newErrors = {};
    if (!formData.otp) {
      newErrors.otp = 'OTP is required';
    } else if (formData.otp.trim().length < 5) {
      newErrors.otp = 'Please enter valid otp !';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.otp && formData.otp.trim().length < 5) {
        wrongOtpError({ action: "wrong_otp", errorMsg: "Please enter valid otp !" });
      }
      return;
    }

    setIsLoading(true);

    resetPassword({
      email: formData.email,
      otp: formData.otp,
      "new-password": formData.password
    })
      .then((res) => {
        if (res.error) {
          const errorMessage = res.error.message;
          if (errorMessage.toLowerCase().includes('otp') || errorMessage.toLowerCase().includes('invalid')) {
            setErrors({ otp: 'OTP entered is invalid' });
            wrongOtpError({ action: "wrong_otp", errorMsg: 'OTP entered is invalid' });
          } else {
            setErrors({ password: errorMessage });
            wrongOtpError({ action: "reset_password", errorMsg: errorMessage });
          }
          return;
        }

        setCurrentStep(STEPS.SUCCESS);
        if (typeof window !== "undefined" && window.dataLayer) {
          window.dataLayer.push({
            event: "dl_forgotpwd_steps",
            event_action: "verify_signin_click",
          });
        }

        // Auto logout and redirect for logged-in users
        if (member) {
          handleLogoutAndRedirect();
        }
      })
      .catch((err) => {
        console.error('Error resetting password:', err);
        setCurrentStep(STEPS.ERROR);
        wrongOtpError({ action: "reset_password", errorMsg: err });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle Enter key for email input step
  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' && !isLoading && formData.email.trim()) {
      e.preventDefault();
      handleGetOTP();
    }
  };

  // Handle Enter key for OTP/password step
  const handleOTPPasswordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const isOTPValid = formData.otp.trim().length >= 5;
      const isFormValid = isOTPValid && formData.password.length >= 8;
      if (isFormValid && !isLoading) {
        handleResetPassword();
      }
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const renderEmailInputStep = () => (
    <div styleName="modal-content desktop">
      <button styleName="close-button" onClick={onClose} type="button">
        <Icon name="close" size="xsmall" />
      </button>

      <h2 styleName="modal-title">{RESET_PASSWORD_CONTENT.TITLE}</h2>

      <div styleName="modal-body">
        <div styleName="form-field">
          <div styleName={`input-container ${errors.email ? 'error' : ''} ${isEmailEditable ? 'active' : (formData.email && !isEmailEditable) ? 'readonly' : ''}`}>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              onKeyDown={handleEmailKeyDown}
              placeholder="Enter your email address"
              readOnly={!isEmailEditable && formData.email}
              styleName={`form-input ${errors.email ? 'error' : ''} ${isEmailEditable ? 'active' : (formData.email && !isEmailEditable) ? 'readonly' : ''}`}
            />
            {formData.email && !isEmailEditable && (
              <button
                type="button"
                onClick={() => setIsEmailEditable(true)}
                styleName="button-base"
                title="Edit email"
              >
                <EditIcon color="#242424" />
              </button>
            )}
          </div>
          {errors.email && (
            <span styleName="error-message">
              {errors.email}
            </span>
          )}
        </div>

        <div styleName="form-actions">
          <Button
            variant="dark"
            onClick={handleGetOTP}
            disabled={isLoading}
            styleName="action-button"
          >
            {isLoading ? RESET_PASSWORD_CONTENT.SENDING : RESET_PASSWORD_CONTENT.GET_OTP}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderOTPPasswordStep = () => {
    const isOTPValid = formData.otp.trim().length >= 5;
    const isFormValid = isOTPValid && formData.password.length >= 8;

    return (
      <div styleName="modal-content desktop">
        <button styleName="close-button" onClick={onClose} type="button">
          <Icon name="close" size="xsmall" />
        </button>

        <h2 styleName="modal-title">{RESET_PASSWORD_CONTENT.TITLE}</h2>

        <div styleName="modal-body">
          {/* OTP Field */}
          <div styleName="form-field">
            <div styleName={`input-container ${errors.otp ? 'error' : ''}`}>
              <input
                type="text"
                value={formData.otp}
                onChange={(e) => handleInputChange('otp', e.target.value)}
                onKeyDown={handleOTPPasswordKeyDown}
                placeholder={RESET_PASSWORD_CONTENT.OTP_PLACEHOLDER}
                styleName={`form-input otp-input ${errors.otp ? 'error' : ''}`}
                maxLength="6"
              />
            </div>
            <div styleName="input-hint">
              {isResendEnabled ? (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  styleName="resend-button"
                  disabled={isLoading}
                >
                  {RESET_PASSWORD_CONTENT.RESEND_OTP}
                </button>
              ) : (
                <span styleName="timer-text">
                  {RESET_PASSWORD_CONTENT.RESEND_OTP} in {formatTime(resendTimer)}
                </span>
              )}
            </div>
            {errors.otp && (
              <span styleName="error-message">
                {errors.otp}
              </span>
            )}
          </div>

          {/* Password Field */}
          <div styleName="form-field">
            <div styleName={`input-container ${errors.password ? 'error' : ''} ${!isOTPValid ? 'disabled' : ''}`}>
              {isPasswordVisible ? (
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onKeyDown={handleOTPPasswordKeyDown}
                  placeholder={RESET_PASSWORD_CONTENT.PASSWORD_PLACEHOLDER}
                  disabled={!isOTPValid}
                  styleName={`form-input password-input ${errors.password ? 'error' : ''} ${!isOTPValid ? 'disabled' : ''}`}
                />
              ) : (
                <div styleName="password-dots-container">
                  {formData.password && (
                    <div styleName="password-dots">
                      {Array.from({ length: formData.password.length }).map((_, index) => (
                        <div key={index} styleName="password-dot" />
                      ))}
                      {isPasswordFocused && <div styleName="password-cursor" />}
                    </div>
                  )}
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    onKeyDown={handleOTPPasswordKeyDown}
                    placeholder={RESET_PASSWORD_CONTENT.PASSWORD_PLACEHOLDER}
                    disabled={!isOTPValid}
                    styleName={`form-input password-input ${formData.password ? 'password-hidden' : ''} ${errors.password ? 'error' : ''} ${!isOTPValid ? 'disabled' : ''}`}
                    autoComplete="new-password"
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                styleName="password-toggle"
                disabled={!isOTPValid}
              >
                {isPasswordVisible ? <OffEye color={isOTPValid ? "#242424" : "#BFBFBF"} />: <OpenEye color={isOTPValid ? "#242424" : "#BFBFBF"}/>}
              </button>
            </div>
            {errors.password && (
              <span styleName="error-message">
                {errors.password}
              </span>
            )}
          </div>

          <div styleName="form-actions">
            <Button
              variant={isFormValid ? "dark" : "disabled"}
              onClick={handleResetPassword}
              disabled={!isFormValid || isLoading}
              styleName="action-button"
            >
              {isLoading ? RESET_PASSWORD_CONTENT.RESETTING : RESET_PASSWORD_CONTENT.TITLE}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderSuccessStep = () => (
    <div styleName="modal-content success">
      <button styleName="close-button" onClick={onClose} type="button">
        <Icon name="close" size="xsmall" />
      </button>
      <h2 styleName="success-title">{RESET_PASSWORD_CONTENT.SUCCESS_TITLE}</h2>
    </div>
  );

  const renderErrorStep = () => (
    <div styleName="modal-content error">
      <button styleName="close-button" onClick={onClose} type="button">
        <Icon name="close" size="xsmall" />
      </button>
      <h2 styleName="error-title">{RESET_PASSWORD_CONTENT.ERROR_TITLE}</h2>
      <p styleName="error-text">{RESET_PASSWORD_CONTENT.ERROR_MESSAGE}</p>
      <Button
        variant="dark"
        onClick={() => setCurrentStep(STEPS.EMAIL_INPUT)}
        styleName="action-button"
      >
        {RESET_PASSWORD_CONTENT.TRY_AGAIN}
      </Button>
    </div>
  );

  const renderMobileView = () => (
    <div styleName="modal-content mobile">
      <button styleName="close-button" onClick={onClose} type="button">
        <Icon name="close" size="xsmall" />
      </button>

      {currentStep !== STEPS.SUCCESS && <h2 styleName="modal-title">{RESET_PASSWORD_CONTENT.TITLE}</h2>}

      <div styleName="modal-body">
        {currentStep === STEPS.EMAIL_INPUT && (
          <>
            <div styleName="form-field">
              <div styleName={`input-container ${errors.email ? 'error' : ''} ${isEmailEditable ? 'active' : (formData.email && !isEmailEditable) ? 'readonly' : ''}`}>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder={RESET_PASSWORD_CONTENT.EMAIL_PLACEHOLDER}
                  readOnly={!isEmailEditable && formData.email}
                  styleName={`form-input ${errors.email ? 'error' : ''} ${isEmailEditable ? 'active' : (formData.email && !isEmailEditable) ? 'readonly' : ''}`}
                />
                {formData.email && !isEmailEditable && (
                  <button
                    type="button"
                    onClick={() => setIsEmailEditable(true)}
                    styleName="button-base"
                    title="Edit email"
                  >
                    <EditIcon color="#242424" />
                  </button>
                )}
              </div>
              {errors.email && (
                <span styleName="error-message">
                  {errors.email}
                </span>
              )}
            </div>

            <div styleName="form-actions">
              <Button
                variant="dark"
                onClick={handleGetOTP}
                disabled={isLoading}
                styleName="action-button"
              >
                {isLoading ? RESET_PASSWORD_CONTENT.SENDING : RESET_PASSWORD_CONTENT.GET_OTP}
              </Button>
            </div>
          </>
        )}

        {currentStep === STEPS.OTP_PASSWORD && (
          <>
            {/* OTP Field */}
            <div styleName="form-field">
              <div styleName={`input-container ${errors.otp ? 'error' : ''}`}>
                <input
                  type="text"
                  value={formData.otp}
                  onChange={(e) => handleInputChange('otp', e.target.value)}
                  placeholder={RESET_PASSWORD_CONTENT.OTP_PLACEHOLDER}
                  styleName={`form-input otp-input ${errors.otp ? 'error' : ''}`}
                  maxLength="6"
                />
              </div>
              <div styleName="input-hint">
                {isResendEnabled ? (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    styleName="resend-button"
                    disabled={isLoading}
                  >
                    {RESET_PASSWORD_CONTENT.RESEND_OTP}
                  </button>
                ) : (
                  <span styleName="timer-text">
                    {RESET_PASSWORD_CONTENT.RESEND_OTP} in {formatTime(resendTimer)}
                  </span>
                )}
              </div>
              {errors.otp && (
                <span styleName="error-message">
                  {errors.otp}
                </span>
              )}
            </div>

            {/* Password Field */}
            <div styleName="form-field">
              <div styleName={`input-container ${errors.password ? 'error' : ''} ${formData.otp.trim().length < 5 ? 'disabled' : ''}`}>
                {isPasswordVisible ? (
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={RESET_PASSWORD_CONTENT.PASSWORD_PLACEHOLDER}
                    disabled={formData.otp.trim().length < 5}
                    styleName={`form-input password-input ${errors.password ? 'error' : ''} ${formData.otp.trim().length < 5 ? 'disabled' : ''}`}
                  />
                ) : (
                  <div styleName="password-dots-container">
                    {formData.password && (
                      <div styleName="password-dots">
                        {Array.from({ length: formData.password.length }).map((_, index) => (
                          <div key={index} styleName="password-dot" />
                        ))}
                        {isPasswordFocused && <div styleName="password-cursor" />}
                      </div>
                    )}
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      onFocus={() => setIsPasswordFocused(true)}
                      onBlur={() => setIsPasswordFocused(false)}
                      placeholder={!formData.password ? RESET_PASSWORD_CONTENT.PASSWORD_PLACEHOLDER : ""}
                      disabled={formData.otp.trim().length < 5}
                      styleName={`form-input password-input ${formData.password ? 'password-hidden' : ''} ${errors.password ? 'error' : ''} ${formData.otp.trim().length < 5 ? 'disabled' : ''}`}
                      autoComplete="new-password"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  styleName="password-toggle"
                  disabled={formData.otp.trim().length < 5}
                >
                  {isPasswordVisible ? <OffEye color={formData.otp.trim().length >= 5 ? "#242424" : "#BFBFBF"} />: <OpenEye color={formData.otp.trim().length >= 5 ? "#242424" : "#BFBFBF"}/>}
                </button>
              </div>
              {errors.password && (
                <span styleName="error-message">
                  {errors.password}
                </span>
              )}
            </div>

            <div styleName="form-actions">
              <Button
                variant={formData.otp.trim().length >= 5 && formData.password.length >= 8 ? "dark" : "disabled"}
                onClick={handleResetPassword}
                disabled={formData.otp.trim().length < 5 || formData.password.length < 8 || isLoading}
                styleName="action-button"
              >
                {isLoading ? RESET_PASSWORD_CONTENT.RESETTING : RESET_PASSWORD_CONTENT.TITLE}
              </Button>
            </div>
          </>
        )}

        {currentStep === STEPS.SUCCESS && (
          <h2 styleName="success-title">{RESET_PASSWORD_CONTENT.SUCCESS_TITLE}</h2>
        )}

        {currentStep === STEPS.ERROR && (
          <>
            <h2 styleName="error-title">{RESET_PASSWORD_CONTENT.ERROR_TITLE}</h2>
            <p styleName="error-text">{RESET_PASSWORD_CONTENT.ERROR_MESSAGE}</p>
            <Button
              variant="dark"
              onClick={() => setCurrentStep(STEPS.EMAIL_INPUT)}
              styleName="action-button"
            >
              {RESET_PASSWORD_CONTENT.TRY_AGAIN}
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div styleName="modal-overlay" className={className} onClick={handleOverlayClick} {...props}>
      {/* Desktop view */}
      <div styleName="desktop-view">
        {currentStep === STEPS.EMAIL_INPUT && renderEmailInputStep()}
        {currentStep === STEPS.OTP_PASSWORD && renderOTPPasswordStep()}
        {currentStep === STEPS.SUCCESS && renderSuccessStep()}
        {currentStep === STEPS.ERROR && renderErrorStep()}
      </div>

      {/* Mobile view */}
      <div styleName="mobile-view">
        {renderMobileView()}
      </div>
    </div>
  );
};

ResetPasswordModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  className: string
};